import { Modal } from "components";
import { FieldGroup } from "components";
import { type Employee, EmployeeModel } from "models";
import type { ModelInstance } from "models/Model";
import { useState } from "react";
import { Button, Confirm, Form, Select } from "semantic-ui-react";
import { USER_MSG } from "strings";
import { useT } from "i18n";
import { useEmployeeMutations } from "hooks/mutations";

interface EditDeleteModalProps {
  employee: ModelInstance<Employee> | null;
  onClose: () => void;
  companyMappings: { value: string; text: string }[];
  setEditModal: (employee: ModelInstance<Employee> | null) => void;
  hasEditAccess: boolean;
  hasDeleteAccess: boolean;
  hasAccessToCompanyDropdown: boolean;
}

export default function EditDeleteModal({
  employee,
  onClose,
  companyMappings,
  setEditModal,
  hasEditAccess,
  hasDeleteAccess,
  hasAccessToCompanyDropdown,
}: EditDeleteModalProps) {
  const t = useT();

  const {
    deleteEmployee,
    deletePending,
    resetEmployeePassword,
    resetPasswordPending,
    editEmployee,
    editPending,
  } = useEmployeeMutations();

  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  const isOpen = !!employee?.data.id;
  const isLoading = resetPasswordPending || deletePending || editPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("section.employee.editEmployee", "Edit Employee")}
      actions={
        <div className={"controls"}>
          <Button
            basic
            content={t("component.common.cancel", "Cancel")}
            onClick={onClose}
          />
          {hasDeleteAccess && employee?.data.id && (
            <Button
              basic
              content={t("section.employee.sendResetEmail", "Send Reset Email")}
              disabled={isLoading}
              loading={resetPasswordPending}
              onClick={() => {
                if (employee?.data.email) {
                  resetEmployeePassword(employee?.data.email);
                }
              }}
            />
          )}
          {hasDeleteAccess && employee?.data.id && (
            <Button
              negative
              content={t("section.employee.delete", "Delete")}
              onClick={() => {
                setDeleteConfirmation(true);
              }}
              loading={deletePending}
              disabled={isLoading}
            />
          )}
          {hasEditAccess && (
            <Button
              primary
              content={t("section.employee.update", "Update")}
              className={"control-right"}
              disabled={isLoading || !employee?.data}
              loading={editPending}
              onClick={() => {
                if (employee?.data) {
                  editEmployee(employee.data, {
                    onSuccess: () => {
                      onClose();
                    },
                  });
                }
              }}
            />
          )}
        </div>
      }
    >
      <Form className={"row"} style={{ gap: "22px", justifyContent: "space-evenly" }}>
        <Confirm
          className={"employee-delete-modal"}
          size={"mini"}
          open={deleteConfirmation}
          onCancel={() => {
            setDeleteConfirmation(false);
          }}
          onConfirm={() => {
            if (employee?.data.id) {
              deleteEmployee(employee?.data.id, {
                onSuccess: () => {
                  onClose();
                },
              });
              setDeleteConfirmation(false);
            }
          }}
          header={USER_MSG.CONFIRM_DELETE_NAME.title.replace("{name}", "Employee")}
          content={USER_MSG.CONFIRM_DELETE_NAME.msg.replace(
            "{name}",
            employee?.data.name || "",
          )}
          confirmButton={"Delete"}
        />
        <FieldGroup
          fields={"name phone isActive"}
          disabled={!hasEditAccess}
          model={employee as ModelInstance<Employee>}
        />
        <div>
          {hasAccessToCompanyDropdown && (
            <div className={"field"}>
              <label>{t("section.employee.company", "Company")}</label>
              <Select
                search
                placeholder={
                  companyMappings.find(
                    (company: { value: string; text: string }) =>
                      company.value === employee?.data.dealerId,
                  )?.text || "Select Company"
                }
                options={companyMappings}
                onChange={(_, { value }: any) => {
                  setEditModal(
                    EmployeeModel({
                      ...employee?.data,
                      dealerId: value,
                    }),
                  );
                }}
                value={employee?.data.dealerId}
                forceSelection={false}
                selectOnBlur={false}
                data-cy={"employee-status-select"}
              />
            </div>
          )}
          <FieldGroup
            fields={"role email"}
            disabled={!hasEditAccess}
            model={employee as ModelInstance<Employee>}
          />
        </div>
      </Form>
    </Modal>
  );
}
